import { useState } from "react";
import { toast } from "react-toastify";
import { requestAuth } from "../../components/services/RequestService";
import { Button } from "../../components/Buttons";
import config from "../../config.json";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Request } from "../../hooks/Request";
import { numberWithDecimals } from "../../utils/validate";
import { listaTributos } from "../../utils/constant";

const styles = StyleSheet.create({
  viewer: {
    width: 900,
    height: window.innerHeight - 200,
  },
  page: {
    padding: "32pt 40pt",
    width: "100%",
    fontSize: 10,
    flexDirection: "column",
  },
  header: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  logoContainer: {
    width: 150,
    alignItems: "flex-start",
  },
  logo: {
    height: 57,
    width: 150,
    objectFit: "contain",
  },
  titleContainer: {
    flex: 1,
    alignItems: "center",
    marginTop: 8,
  },
  mainTitle: {
    fontFamily: "Helvetica-Bold",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  headerRight: {
    flex: 1,
    alignItems: "flex-end",
    marginTop: 8,
    fontSize: 10,
  },
  infoSection: {
    marginBottom: 8,
    fontSize: 10,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  infoLabel: {
    fontFamily: "Helvetica-Bold",
    marginRight: 4,
  },
  glossSection: {
    border: "1pt solid #666",
    padding: 8,
    marginBottom: 12,
    minHeight: 40,
    fontFamily: "Helvetica-Bold",
  },
  tableContainer: {
    width: "100%",
    marginBottom: 8,
  },
  tableHeader: {
    width: "100%",
    flexDirection: "row",
    border: "1pt solid #666",
    borderTop: "none",
  },
  headerCell: {
    textAlign: "center",
    borderRight: "1pt solid #666",
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    paddingTop: 4,
    paddingBottom: 15,
  },
  codeHeader: {
    width: "14%",
  },
  accountHeader: {
    flex: 1,
  },
  amountHeader: {
    width: "12%",
  },
  currencyHeader: {
    width: "24%",
    textAlign: "center",
    borderRight: "1pt solid #666",
    paddingTop: 2,
    paddingBottom: 8,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    borderBottom: "1pt solid #666",
  },
  tableRow: {
    width: "100%",
    flexDirection: "row",
    borderLeft: "1pt solid #666",
    borderRight: "1pt solid #666",
  },
  cell: {
    borderRight: "1pt solid #666",
    padding: "2pt 4pt",
  },
  codeCell: {
    width: "14%",
    fontSize: 9,
    lineHeight: 1.5,
  },
  accountCell: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.5,
  },
  amountCell: {
    width: "12%",
    textAlign: "right",
    fontSize: 9,
    paddingRight: 6,
  },
  footer: {
    width: "100%",
    flexDirection: "row",
    border: "1pt solid #666",
    backgroundColor: "#f5f5f5",
  },
  descriptionLabel: {
    width: "14%",
    fontFamily: "Helvetica-Bold",
    padding: 8,
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    fontFamily: "Helvetica-Bold",
    paddingRight: 10,
    padding: 8,
    borderRight: "1pt solid #666",
  },
  totalAmount: {
    width: "12%",
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    paddingRight: 6,
    padding: 8,
    borderRight: "1pt solid #666",
  },
});

const DialogPerformAccounting = (props) => {
  const { item, onClose, handleSuccess = () => {} } = props;
  const [load, setload] = useState(false);

  async function handleOnSubmit() {
    setload(true);

    try {
      const dropdownResponse = await requestAuth(
        "get",
        `${config.requestURLConta}/api/Account/subfijo/0?nro=${Number(
          item.det_nroSolPlanilla,
        )}`,
      );
      const subfijo = dropdownResponse.data;

      if (!subfijo.cuentaContable || subfijo.cuentaContable.trim() === "") {
        setload(false);
        toast.error(
          `La opción contable para "${item.det_nroSolPlanilla}" no tiene un código contable válido`,
        );
        return;
      }

      let subDataResponse = { data: { id: 0 } };
      const subDataIds = [];

      if (item.label === "PAGO DE TRIBUTOS ADUANEROS") {
        const tributosConMonto = listaTributos.filter(
          (tb) => item?.[tb.name] && Number(item?.[tb.name]) > 0,
        );

        for (const tributo of tributosConMonto) {
          const subData = await requestAuth(
            "post",
            `/dataSet/procedure/${item.procedureId}/fieldname/a-TributosYOtrosConceptosAduaneros/subdata`,
            {
              label: `${tributo.label}`,
              description: Number(item?.[tributo.name]),
              description2: Number(item?.[tributo.name]),
              description3: subfijo.cuentaContable,
              description4: "true",
              details: item.det_description,
            },
          );
          subDataIds.push(subData.data.id);
        }

        if (tributosConMonto.length === 0) {
          subDataResponse = await requestAuth(
            "post",
            `/dataSet/procedure/${item.procedureId}/fieldname/a-TributosYOtrosConceptosAduaneros/subdata`,
            {
              label: item.label,
              description: 0,
              description2: 0,
              description3: subfijo.cuentaContable,
              description4: "true",
              details: item.det_description,
            },
          );
          subDataIds.push(subDataResponse.data.id);
        } else {
          subDataResponse = { data: { id: subDataIds[0] } };
        }
      } else {
        subDataResponse = await requestAuth(
          "post",
          `/dataSet/procedure/${item.procedureId}/fieldname/a-TributosYOtrosConceptosAduaneros/subdata`,
          {
            label: item.label,
            description: item.numVal,
            description2: item.numVal,
            description3: subfijo.cuentaContable,
            description4: "true",
            details: item.det_description,
          },
        );
        subDataIds.push(subDataResponse.data.id);
      }

      const currentDetails = JSON.parse(item.details || "{}");

      const amountList =
        item.label === "PAGO DE TRIBUTOS ADUANEROS"
          ? listaTributos
              .map((tb) => ({
                label: tb.label,
                amount: item?.[tb.name] ? Number(item?.[tb.name]) : 0,
              }))
              .filter((tb) => tb.amount > 0)
          : [];

      if (item.label === "PAGO DE TRIBUTOS ADUANEROS") {
        const voucherResponse = await requestAuth(
          "post",
          `${config.requestURLConta}/api/Voucher/spreadsheet-step`,
          {
            procedureId: `${item.procedureId}`,
            voucherId: 0,
            amount: item.numVal,
            gloss: item.det_concepto,
            codeAccount: subfijo.cuentaContable,
            codeAccountTrazo: item.det_banco,
            subDataId: subDataIds[0],
            transactionNumber: "",
            paymentMethod: "cheque",
            CheckNum: item.det_nroCheque,
            CheckTo: item.det_chequePara,
            amountList: amountList,
            label: item.label,
            description: item.det_description,
          },
        );

        const voucherId = voucherResponse.data;

        const updatedDetails = {
          ...currentDetails,
          subDataIds: subDataIds,
          voucherId: voucherId,
        };

        await requestAuth("put", "/ProcElement", {
          ...item,
          details: JSON.stringify(updatedDetails),
        });
      } else {
        const voucherResponse = await requestAuth(
          "post",
          `${config.requestURLConta}/api/Voucher/spreadsheet-step`,
          {
            procedureId: `${item.procedureId}`,
            voucherId: 0,
            amount: item.numVal,
            gloss: item.det_concepto,
            codeAccount: subfijo.cuentaContable,
            codeAccountTrazo: item.det_banco,
            subDataId: subDataResponse.data.id,
            transactionNumber: "",
            paymentMethod: "cheque",
            CheckNum: item.det_nroCheque,
            CheckTo: item.det_chequePara,
            amountList: amountList,
            label: item.label,
            description: item.det_description,
          },
        );

        const voucherId = voucherResponse.data;

        const updatedDetails = {
          ...currentDetails,
          subDataId: subDataResponse.data.id,
          voucherId: voucherId,
        };

        await requestAuth("put", "/ProcElement", {
          ...item,
          details: JSON.stringify(updatedDetails),
        });
      }

      setload(false);
      toast.success("Se guardaron los cambios exitosamente");
      onClose();
      handleSuccess();
    } catch (err) {
      setload(false);
      console.log(err);
      toast.error(err?.response?.data?.message || "No se pudo guardar");
    }
  }

  return (
    <div>
      {item.det_banco && item.det_nroSolPlanilla ? (
        <>
          <div className="mb-4">
            {load ? (
              <div className="w-full h-[100px] flex justify-center items-center">
                <svg className="animate-spin h-[30px] mr-3" viewBox="0 0 24 24">
                  <i className="text-white font-bold fa-solid fa-circle-notch"></i>
                </svg>
              </div>
            ) : (
              <div className="flex gap-4">
                <Button
                  onClick={onClose}
                  className="w-full bg-[#fff] text-[#FBCE00] border-2 border-[#FBCE00]"
                >
                  Cancelar
                </Button>
                <Button onClick={handleOnSubmit} className="w-full px-4 py-2">
                  Aprobar Egreso
                </Button>
              </div>
            )}
          </div>
          <VoucherDocumentPDF item={item} />
        </>
      ) : (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-600">
            No se puede generar la previsualización. Faltan datos del banco o
            número de planilla.
          </p>
        </div>
      )}
    </div>
  );
};

export const VoucherDocumentPDF = ({ item }) => {
  const {
    data: dataSub,
    loading: loadingSub,
    error: errorSub,
  } = Request({
    urlApi: `${config.requestURLConta}/api/Account/subfijo/0?nro=${item.det_nroSolPlanilla}`,
  });

  const { data, loading, error } = Request({
    urlApi: `${config.requestURLConta}/api/Account/All`,
  });

  if (!loading || !loadingSub) {
    return (
      <div className="w-full h-[500px] flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3">Cargando datos del voucher...</span>
      </div>
    );
  }

  if (errorSub) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">
          Error: No se encontró el subfijo nro "{item.det_nroSolPlanilla}"
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">
          Error al cargar datos de cuentas contables
        </p>
      </div>
    );
  }

  if (!data || !Array.isArray(data)) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-600">Formato de datos de cuentas no válido</p>
      </div>
    );
  }

  const getTributosConMonto = () => {
    return listaTributos
      .map((tb) => ({
        label: tb.label,
        amount: item?.[tb.name] ? Number(item?.[tb.name]) : 0,
      }))
      .filter((tb) => tb.amount > 0);
  };

  const getBankAccountHierarchy = (targetCode, accounts) => {
    const findPath = (node, path = []) => {
      const currentPath = [
        ...path,
        {
          code: node.code,
          description: node.description,
          level: path.length + 1,
        },
      ];

      if (node.code === targetCode) {
        return currentPath;
      }

      if (node.accountChild && Array.isArray(node.accountChild)) {
        for (const child of node.accountChild) {
          const result = findPath(child, currentPath);
          if (result) return result;
        }
      }

      return null;
    };

    for (const account of accounts) {
      const path = findPath(account);
      if (path) return path;
    }

    return [
      {
        code: targetCode,
        description: "Cuenta Bancaria",
        level: 1,
      },
    ];
  };

  const prepareTableItems = () => {
    const items = [];
    const isTributos = item.label === "PAGO DE TRIBUTOS ADUANEROS";
    const tributos = getTributosConMonto();
    const exchangeRate = 6.96;

    const subfijoInfo = dataSub || {};
    const cuentasPadreSubfijo = subfijoInfo.cuentasPadre || [];

    const bankAccountHierarchy = getBankAccountHierarchy(item.det_banco, data);

    if (isTributos && tributos.length > 0) {
      tributos.forEach((tributo) => {
        const cuentasPadreFiltradas = cuentasPadreSubfijo.filter(
          (cuenta) => cuenta.level > 3,
        );
        cuentasPadreFiltradas.forEach((cuenta, index) => {
          items.push({
            id: `subfijo-parent-${cuenta.code}-${tributo.label}`,
            code: cuenta.code,
            description: cuenta.description,
            debitBs: index === 0 ? tributo.amount : 0,
            assetBs: 0,
            debitSus: index === 0 ? tributo.amount / exchangeRate : 0,
            assetSus: 0,
          });
        });

        items.push({
          id: `subfijo-${subfijoInfo.cuentaContable}-${tributo.label}`,
          code: subfijoInfo.cuentaContable || "",
          description: tributo.label,
          debitBs: 0,
          assetBs: 0,
          debitSus: 0,
          assetSus: 0,
        });
      });

      const bankAccountsFiltradas = bankAccountHierarchy.filter(
        (cuenta) => cuenta.level >= 3,
      );

      bankAccountsFiltradas.forEach((cuenta, index) => {
        items.push({
          id: `banco-parent-${cuenta.code}`,
          code: cuenta.code,
          description: cuenta.description,
          debitBs: 0,
          assetBs: index === 0 ? item.numVal : 0,
          debitSus: 0,
          assetSus: index === 0 ? item.numVal / exchangeRate : 0,
        });
      });
    } else {
      const cuentasPadreFiltradas = cuentasPadreSubfijo.filter(
        (cuenta) => cuenta.level > 3,
      );

      cuentasPadreFiltradas.forEach((cuenta, index) => {
        items.push({
          id: `subfijo-parent-${cuenta.code}`,
          code: cuenta.code,
          description: cuenta.description,
          debitBs: index === 0 ? item.numVal : 0,
          assetBs: 0,
          debitSus: index === 0 ? item.numVal / exchangeRate : 0,
          assetSus: 0,
        });
      });

      items.push({
        id: `subfijo-${subfijoInfo.cuentaContable}-${
          cuentasPadreFiltradas[cuentasPadreFiltradas.length - 1].description
        }`,
        code: subfijoInfo.cuentaContable || "",
        description:
          cuentasPadreFiltradas[cuentasPadreFiltradas.length - 1].description,
        debitBs: 0,
        assetBs: 0,
        debitSus: 0,
        assetSus: 0,
      });

      const bankAccountsFiltradas = bankAccountHierarchy.filter(
        (cuenta) => cuenta.level >= 3,
      );

      bankAccountsFiltradas.forEach((cuenta, index) => {
        items.push({
          id: `banco-parent-${cuenta.code}`,
          code: cuenta.code,
          description: cuenta.description,
          debitBs: 0,
          assetBs: index === 0 ? item.numVal : 0,
          debitSus: 0,
          assetSus: index === 0 ? item.numVal / exchangeRate : 0,
        });
      });
    }

    return items;
  };

  const tableItems = prepareTableItems();

  const totalDebitBs = tableItems.reduce(
    (sum, item) => sum + (item.debitBs || 0),
    0,
  );
  const totalAssetBs = tableItems.reduce(
    (sum, item) => sum + (item.assetBs || 0),
    0,
  );
  const totalDebitSus = tableItems.reduce(
    (sum, item) => sum + (item.debitSus || 0),
    0,
  );
  const totalAssetSus = tableItems.reduce(
    (sum, item) => sum + (item.assetSus || 0),
    0,
  );

  const formatDate = () => {
    return dayjs().locale("es").format("DD/MM/YYYY");
  };

  const processItemLines = (item) => {
    const codeLines = String(item?.code || "")
      .split("\n")
      .filter((line) => line.trim());

    const descLines = String(item?.description || "")
      .split("\n")
      .filter((line) => line.trim());

    if (codeLines.length === 0 && descLines.length === 0) {
      return [
        {
          code: item?.code || "",
          desc: item?.description || "",
        },
      ];
    }

    const maxLines = Math.max(codeLines.length, descLines.length);
    const rows = [];

    for (let i = 0; i < maxLines; i++) {
      rows.push({
        code: codeLines[i] || "",
        desc: descLines[i] || "",
      });
    }

    return rows;
  };

  return (
    <div className="border rounded-lg shadow-lg overflow-hidden">
      <PDFViewer style={styles.viewer}>
        <Document>
          <Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <View style={styles.logoContainer}>
                <Image
                  src="https://res.cloudinary.com/degnu2idl/image/upload/v1682460459/gamarra/LOS_ANDES_GAMARRA_qmlzlz.png"
                  style={styles.logo}
                />
              </View>

              <View style={styles.titleContainer}>
                <Text style={styles.mainTitle}>COMPROBANTE DE EGRESO</Text>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Fecha:</Text>
                  <Text>{formatDate()}</Text>
                </View>
              </View>

              <View style={styles.headerRight}>
                <Text style={{ fontWeight: "bold" }}>CE-</Text>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>T/C Dolar:</Text>
                  <Text>6.96</Text>
                </View>
              </View>
            </View>

            <View style={styles.infoSection}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Cheque No.</Text>
                <Text>{item.det_nroCheque || "-"}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Cheque girado a:</Text>
                <Text>{item.det_chequePara || "-"}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Glosa:</Text>
                <Text>{item.det_concepto || item.label}</Text>
              </View>
            </View>

            <View style={styles.glossSection}>
              <Text>{item.det_concepto || item.label}</Text>
            </View>

            <View style={styles.tableContainer}>
              <View
                style={{
                  flexDirection: "row",
                  border: "1pt solid #666",
                  borderBottom: "none",
                }}
              >
                <Text style={[styles.headerCell, styles.codeHeader]}></Text>
                <Text style={[styles.headerCell, styles.accountHeader]}></Text>
                <Text style={styles.currencyHeader}>BOLIVIANOS</Text>
                <Text style={[styles.currencyHeader, { borderRight: "none" }]}>
                  DOLARES
                </Text>
              </View>

              <View style={styles.tableHeader}>
                <Text style={[styles.headerCell, styles.codeHeader]}>
                  CÓDIGO
                </Text>
                <Text style={[styles.headerCell, styles.accountHeader]}>
                  DETALLE
                </Text>
                <Text style={[styles.headerCell, styles.amountHeader]}>
                  DEBE
                </Text>
                <Text style={[styles.headerCell, styles.amountHeader]}>
                  HABER
                </Text>
                <Text style={[styles.headerCell, styles.amountHeader]}>
                  DEBE
                </Text>
                <Text
                  style={[
                    styles.headerCell,
                    styles.amountHeader,
                    { borderRight: "none" },
                  ]}
                >
                  HABER
                </Text>
              </View>

              {tableItems.map((tableItem, index) => {
                const rows = processItemLines(tableItem);
                const isLastItem = index === tableItems.length - 1;

                return (
                  <View key={tableItem.id || index}>
                    {rows.map((row, rowIndex) => (
                      <View
                        key={rowIndex}
                        style={[
                          styles.tableRow,
                          rowIndex === rows.length - 1 && isLastItem
                            ? { borderBottom: "1pt solid #666" }
                            : {},
                        ]}
                      >
                        <View style={[styles.cell, styles.codeCell]}>
                          <Text style={{ fontSize: rowIndex > 0 ? 8 : 9 }}>
                            {row.code}
                          </Text>
                        </View>
                        <View style={[styles.cell, styles.accountCell]}>
                          <Text style={{ fontSize: rowIndex > 0 ? 8 : 9 }}>
                            {row.desc}
                          </Text>
                        </View>
                        <Text style={[styles.cell, styles.amountCell]}>
                          {rowIndex === 0 && tableItem.debitBs
                            ? numberWithDecimals(tableItem.debitBs)
                            : ""}
                        </Text>
                        <Text style={[styles.cell, styles.amountCell]}>
                          {rowIndex === 0 && tableItem.assetBs
                            ? numberWithDecimals(tableItem.assetBs)
                            : ""}
                        </Text>
                        <Text style={[styles.cell, styles.amountCell]}>
                          {rowIndex === 0 && tableItem.debitSus
                            ? numberWithDecimals(tableItem.debitSus)
                            : ""}
                        </Text>
                        <Text
                          style={[
                            styles.cell,
                            styles.amountCell,
                            { borderRight: "none" },
                          ]}
                        >
                          {rowIndex === 0 && tableItem.assetSus
                            ? numberWithDecimals(tableItem.assetSus)
                            : ""}
                        </Text>
                      </View>
                    ))}
                  </View>
                );
              })}

              {/* Footer con totales */}
              <View style={styles.footer}>
                <Text style={styles.descriptionLabel}></Text>
                <Text style={styles.totalLabel}>TOTALES:</Text>
                <Text style={styles.totalAmount}>
                  {numberWithDecimals(totalDebitBs)}
                </Text>
                <Text style={styles.totalAmount}>
                  {numberWithDecimals(totalAssetBs)}
                </Text>
                <Text style={styles.totalAmount}>
                  {numberWithDecimals(totalDebitSus)}
                </Text>
                <Text style={[styles.totalAmount, { borderRight: "none" }]}>
                  {numberWithDecimals(totalAssetSus)}
                </Text>
              </View>
            </View>

            {/* Imagen de recibido conforme */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                marginTop: 20,
              }}
            >
              <Image
                style={{ transform: "scale(1.01)" }}
                src="https://res.cloudinary.com/joeldes/image/upload/v1763574235/Gamarra/ad31bf72-d62c-44c5-bdbc-33fcbc22faa5.png"
              />
            </View>
          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default DialogPerformAccounting;
